/**
 * manager-pdp.js
 * Gerencia a Product Detail Page (PDP).
 * Disponível como window._pdpManager (instância atual).
 *
 * Dependências (devem carregar antes):
 *   - window.CartManager    (manager-cart.js)
 *   - window.ButtonFeedback (manager-global-button-feedback.js)
 *
 * ─── Módulos ──────────────────────────────────────────────────────────────
 *
 *   SizeSelectorController  → popup de seleção de tamanho
 *   AddToCartController     → intercepta form submit e delega ao CartManager
 *   GalleryObserver         → ativa âncoras do menu lateral ao scrollar
 *                             (opt-in: seção deve ter data-pdp-gallery)
 *
 * ─── Inicialização via Liquid ─────────────────────────────────────────────
 *
 *   Na section pdp-main.liquid, dentro do bloco {% javascript %}:
 *
 *     if (typeof initPdpManager === 'function') initPdpManager('{{ section.id }}');
 *
 *   A seção é selecionada por .pdpMain--{sectionId}.
 *
 * ─── HTML esperado ────────────────────────────────────────────────────────
 *
 *   <section class="pdpMain--{sectionId}" [data-pdp-gallery]>
 *
 *     <!-- Imagens da galeria (GalleryObserver — opt-in via data-pdp-gallery) -->
 *     <div id="product-image-1">...</div>
 *     <div id="product-image-2">...</div>
 *
 *     <!-- Âncoras do menu lateral -->const result = await window
 *     <a href="#product-image-1" class="pdpMain__bannerMenu">...</a>
 *
 *     <!-- Um ou mais wrappers de produto (ex: produto + produto relacionado) -->
 *     <div data-product-wrapper>
 *
 *       <!-- Seletor de tamanho -->
 *       <div data-size-selector>
 *         <button data-size-toggle aria-expanded="false">Selecionar tamanho</button>
 *         <div data-size-popup hidden>
 *           <button data-size-option data-variant-id="111" data-size-label="P">P</button>
 *           <button data-size-option data-variant-id="222" data-size-label="M">M</button>
 *         </div>
 *         <span data-size-label></span>
 *       </div>
 *
 *       <!-- Formulário de produto -->
 *       <form action="/cart/add">
 *         <input type="hidden" name="id" data-size-input>
 *         <button type="button" data-add-to-cart disabled>Selecione o tamanho</button>
 *       </form>
 *
 *     </div>
 *
 *   </section>
 */

/* ─── SizeSelectorController ──────────────────────────────────────────────── */

class SizeSelectorController {
  constructor(wrapper) {
    this._wrapper  = wrapper;
    this._toggle   = wrapper.querySelector('[data-size-toggle]');
    this._popup    = wrapper.querySelector('[data-size-popup]');
    this._options  = wrapper.querySelectorAll('[data-size-option]');
    this._label    = wrapper.querySelector('[data-size-label]');
    this._input    = wrapper.querySelector('[data-size-input]');
    this._addBtn   = wrapper.querySelector('[data-add-to-cart]');
    this._notify   = wrapper.querySelector('[data-back-in-stock]');
    this._status   = wrapper.querySelector('[data-availability-status] span');
    this._prices   = wrapper.querySelectorAll('[data-pdp-current-price]');
    this._comparePrices = wrapper.querySelectorAll('[data-pdp-compare-price]');
    this._installmentPrices = wrapper.querySelectorAll('[data-pdp-installment-price]');
    this._installmentMoney = wrapper.querySelectorAll('[data-pdp-installment-money]');
    this._section  = wrapper.closest('[data-pdp-section-id]');
    this._discountBadge = this._section?.querySelector('[data-pdp-discount-badge]');
    this._discountValue = this._discountBadge?.querySelector('[data-pdp-discount-value]');
    this._isOpen   = false;

    this._onDocClick = this._onDocClick.bind(this);
    this._onKeyDown  = this._onKeyDown.bind(this);

    this._init();
  }

  _init() {
    this._toggle?.addEventListener('click', () => this._isOpen ? this._close() : this._open());
    this._options.forEach(opt => opt.addEventListener('click', () => this._select(opt)));
    this._notify?.querySelector('[data-notify-me]')?.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('pdp:notify-me', {
        detail: {
          productId: this._notify.dataset.productId,
          variantId: this._notify.dataset.variantId
        }
      }));
    });
    document.addEventListener('click',   this._onDocClick);
    document.addEventListener('keydown', this._onKeyDown);
  }

  _onDocClick(e) {
    if (!this._wrapper.contains(e.target)) this._close();
  }

  _onKeyDown(e) {
    if (e.key === 'Escape') this._close();
  }

  _open() {
    this._popup?.classList.add('is-open');
    this._popup?.removeAttribute('hidden');
    this._toggle?.setAttribute('aria-expanded', 'true');
    this._isOpen = true;
  }

  _close() {
    this._popup?.classList.remove('is-open');
    this._popup?.setAttribute('hidden', '');
    this._toggle?.setAttribute('aria-expanded', 'false');
    this._isOpen = false;
  }

  _select(option) {
    const variantId   = option.dataset.variantId;
    const label       = option.dataset.sizeLabel || option.textContent.trim();
    const isAvailable = option.dataset.variantAvailable === 'true';

    if (this._input)  this._input.value        = variantId;
    if (this._label)  this._label.textContent   = label;
    if (this._status) this._status.textContent  = this._getAvailabilityLabel(option);
    this._updatePricing(option);
    this._updateVariantUrl(variantId);

    // Marca opção ativa
    this._options.forEach(o => o.classList.toggle('is-active', o === option));

    this._toggleAvailability(isAvailable, variantId);

    this._close();
  }

  _toggleAvailability(isAvailable, variantId) {
    if (this._addBtn) {
      this._addBtn.disabled = !isAvailable;
      this._addBtn.hidden   = !isAvailable;
      this._addBtn.type     = 'submit';
    }

    if (this._notify) {
      this._notify.hidden = isAvailable;
      if (variantId) this._notify.dataset.variantId = variantId;
    }

    document.dispatchEvent(new CustomEvent('pdp:variant:change', {
      detail: { variantId, available: isAvailable }
    }));
  }

  _updateVariantUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState(window.history.state, '', url);
  }

  _getAvailabilityLabel(option) {
    const isAvailable = option.dataset.variantAvailable === 'true';
    const inventoryQuantity = Number.parseInt(option.dataset.variantInventoryQuantity || '0', 10);

    if (!isAvailable) return 'Produto indisponível';
    if (inventoryQuantity === 1) return '1 unidade disponível';
    return 'Produto disponível';
  }

  _updatePricing(option) {
    const hasDiscount = option.dataset.variantHasDiscount === 'true';
    const currentPrice = option.dataset.variantPriceFormatted;
    const compareAtPrice = option.dataset.variantCompareAtPriceFormatted;
    const installmentPrice = option.dataset.variantInstallmentPrice;
    const installmentMoney = option.dataset.variantInstallmentMoney;

    this._prices.forEach(element => {
      if (currentPrice) element.textContent = currentPrice;
    });

    this._comparePrices.forEach(element => {
      if (compareAtPrice) element.textContent = compareAtPrice;
      element.hidden = !hasDiscount;
    });

    this._installmentPrices.forEach(element => {
      if (installmentPrice) element.textContent = installmentPrice;
    });

    this._installmentMoney.forEach(element => {
      if (installmentMoney) element.textContent = installmentMoney;
    });

    if (this._discountBadge) this._discountBadge.hidden = !hasDiscount;
    if (this._discountValue) this._discountValue.textContent = option.dataset.variantDiscount || '0';
  }

  destroy() {
    document.removeEventListener('click',   this._onDocClick);
    document.removeEventListener('keydown', this._onKeyDown);
  }
}

/* ─── AddToCartController ─────────────────────────────────────────────────── */

class AddToCartController {
  constructor(wrapper) {
    this._form   = wrapper.querySelector('form[action*="/cart/add"]');
    this._button = wrapper.querySelector('[data-add-to-cart]');
    // Suporta data-size-input (size selector) ou name="id" (select/hidden input padrão)
    this._input  = wrapper.querySelector('[data-size-input]')
                    || wrapper.querySelector('[name="id"]');

    if (!this._form) return;

    this._onSubmit = this._onSubmit.bind(this);
    this._form.addEventListener('submit', this._onSubmit);
  }

  async _onSubmit(e) {
    e.preventDefault();
    const input =
      this._form.querySelector('[name="id"]:checked') ||
      this._form.querySelector('[name="id"][type="hidden"]');

    if (input?.disabled) return;
    if (input?.dataset.variantAvailable === 'false') return;

    const variantId = input?.getAttribute('data-variant-id') || input?.value;

    if (!variantId) return;

    const action  = this._button?.dataset.addAction || 'open_drawer';
    const cartUrl = this._button?.dataset.cartUrl   || '/cart';

    // Congela dimensões para evitar layout shift ao injetar SVG do ButtonFeedback
    const btn = this._button;
    if (btn) {
      btn.style.width  = `${btn.offsetWidth}px`;
      btn.style.height = `${btn.offsetHeight}px`;
    }

    
const formData = new FormData(this._form);
const properties = {};

for (const [name, value] of formData.entries()) {
  const match = name.match(/^properties\[(.+)\]$/);

  if (match && typeof value === 'string') {
    properties[match[1]] = value;
  }
}

const result = await window.CartManager?.add(variantId, {
  button: btn,
  action,
  properties
});


    if (btn) {
      btn.style.width  = '';
      btn.style.height = '';
    }

    if (result?.success && action === 'open_cart') {
      window.location.href = cartUrl;
    }
  }

  destroy() {
    this._form?.removeEventListener('submit', this._onSubmit);
  }
}

/* ─── GalleryObserver ─────────────────────────────────────────────────────── */

class GalleryObserver {
  constructor(section) {
    this._observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const anchor = section.querySelector(`a.pdpMain__bannerMenu[href="#${entry.target.id}"]`);
          if (!anchor) return;
          anchor.classList.toggle('opacity-100', entry.isIntersecting);
          anchor.classList.toggle('opacity-50',  !entry.isIntersecting);
        });
      },
      { threshold: 0.5 }
    );

    section.querySelectorAll('[id^="product-image-"]').forEach(el => {
      this._observer.observe(el);
    });
  }

  destroy() {
    this._observer.disconnect();
  }
}

/* ─── PdpManager ──────────────────────────────────────────────────────────── */

class PdpManager {
  constructor({ sectionId }) {
    this._section = document.querySelector(`.pdpMain--${sectionId}`);
    if (!this._section) return;

    this._modules = [];

    // Um SizeSelectorController + AddToCartController por [data-product-wrapper]
    this._section.querySelectorAll('[data-product-wrapper]').forEach(wrapper => {
      this._modules.push(new SizeSelectorController(wrapper));
      this._modules.push(new AddToCartController(wrapper));
    });

    // GalleryObserver é opt-in via data-pdp-gallery na seção
    if (this._section.hasAttribute('data-pdp-gallery')) {
      this._modules.push(new GalleryObserver(this._section));
    }
  }

  destroy() {
    this._modules.forEach(m => m.destroy?.());
    this._modules = [];
  }
}

/* ─── Inicialização ───────────────────────────────────────────────────────── */

function initPdpManager(sectionId) {
  if (window._pdpManager) window._pdpManager.destroy();
  window._pdpManager = new PdpManager({ sectionId });
}

window.initPdpManager = initPdpManager;

// Auto-inicialização: escaneia o DOM por seções PDP declaradas via data attribute.
// Resolve o problema de timing onde {% javascript %} executa antes deste script.
document.querySelectorAll('[data-pdp-section-id]').forEach(el => {
  initPdpManager(el.dataset.pdpSectionId);
});

document.addEventListener('shopify:section:load', event => {
  const target = event.target;
  const el = target.matches?.('[data-pdp-section-id]')
    ? target
    : target.querySelector?.('[data-pdp-section-id]');

  if (el) initPdpManager(el.dataset.pdpSectionId);
});
